export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_MAX_LENGTH = 20;

const ASCENDING_SEQUENCES = ['012', '123', '234', '345', '456', '567', '678', '789', '890'];
const DESCENDING_SEQUENCES = ['098', '987', '876', '765', '654', '543', '432', '321', '210'];
const REPEATED_DIGITS = ['000', '111', '222', '333', '444', '555', '666', '777', '888', '999'];

export type PasswordPolicyRuleId =
  | 'required'
  | 'length'
  | 'letter'
  | 'digit'
  | 'sequence'
  | 'repeated';

export type PasswordPolicyRule = {
  id: PasswordPolicyRuleId;
  label: string;
  displayLabel: string;
  test: (password: string) => boolean;
};

export const PASSWORD_POLICY_RULES: PasswordPolicyRule[] = [
  {
    id: 'length',
    label: `Şifre uzunluğu en az ${PASSWORD_MIN_LENGTH}, en fazla ${PASSWORD_MAX_LENGTH} karakter olmalıdır`,
    displayLabel: `${PASSWORD_MIN_LENGTH}-${PASSWORD_MAX_LENGTH} karakter uzunluğunda olmalı`,
    test: (password) =>
      password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
  },
  {
    id: 'letter',
    label: 'Şifre en az bir adet harf içermelidir',
    displayLabel: 'En az 1 harf içermeli',
    test: (password) => /[a-zA-ZğüşıöçĞÜŞİÖÇ]/.test(password),
  },
  {
    id: 'digit',
    label: 'Şifre en az bir adet rakam içermelidir',
    displayLabel: 'En az 1 rakam içermeli',
    test: (password) => /\d/.test(password),
  },
  {
    id: 'sequence',
    label: 'Şifre ardışık rakam dizileri (ör. 123, 321, 012) içermemelidir',
    displayLabel: 'Ardışık sayı dizileri içermemeli (123, 321 vb.)',
    test: (password) =>
      ![...ASCENDING_SEQUENCES, ...DESCENDING_SEQUENCES].some((seq) => password.includes(seq)),
  },
  {
    id: 'repeated',
    label: 'Şifre aynı rakamın art arda 3 kez tekrar ettiği diziler (ör. 111) içermemelidir',
    displayLabel: 'Aynı rakamın 3 kez tekrar ettiği diziler içermemeli',
    test: (password) => !REPEATED_DIGITS.some((seq) => password.includes(seq)),
  },
];

const ERROR_MESSAGES: Record<PasswordPolicyRuleId, string> = {
  required: 'Şifre alanı boş bırakılamaz',
  length: `Şifre uzunluğu en az ${PASSWORD_MIN_LENGTH}, en fazla ${PASSWORD_MAX_LENGTH} karakter olmalıdır`,
  letter: 'Şifre en az bir adet harf içermelidir',
  digit: 'Şifre en az bir adet rakam içermelidir',
  sequence: 'Şifre ardışık rakam dizileri içermemelidir',
  repeated: 'Şifre aynı rakamın art arda 3 kez tekrar ettiği diziler içermemelidir',
};

const RULES_REQUIRING_INPUT_FOR_PASS: PasswordPolicyRuleId[] = ['sequence', 'repeated'];

export function getPasswordPolicyRuleResults(password: string): Record<PasswordPolicyRuleId, boolean> {
  const hasInput = password.length > 0;

  return PASSWORD_POLICY_RULES.reduce(
    (acc, rule) => {
      const passed = rule.test(password);
      acc[rule.id] =
        !hasInput && RULES_REQUIRING_INPUT_FOR_PASS.includes(rule.id) ? false : passed;
      return acc;
    },
    {
      required: password.trim().length > 0,
      length: false,
      letter: false,
      digit: false,
      sequence: false,
      repeated: false,
    } as Record<PasswordPolicyRuleId, boolean>
  );
}

export function isPasswordPolicyValid(password: string): boolean {
  if (!password.trim()) return false;
  return PASSWORD_POLICY_RULES.every((rule) => rule.test(password));
}

export function validatePasswordPolicy(password: string): string | true {
  if (!password?.trim()) {
    return ERROR_MESSAGES.required;
  }

  for (const rule of PASSWORD_POLICY_RULES) {
    if (!rule.test(password)) {
      return ERROR_MESSAGES[rule.id];
    }
  }

  return true;
}

export function getPasswordPolicyRegisterRules(requiredMessage = 'Şifre alanı boş bırakılamaz') {
  return {
    required: requiredMessage,
    validate: (value: string) => validatePasswordPolicy(value),
  };
}

export type PasswordStrengthLevel = 'weak' | 'medium' | 'strong';

export type PasswordStrength = {
  level: PasswordStrengthLevel;
  label: string;
  percent: number;
  color: string;
};

export function getPasswordStrength(password: string): PasswordStrength {
  const ruleResults = getPasswordPolicyRuleResults(password);
  const passedCount = PASSWORD_POLICY_RULES.filter((rule) => ruleResults[rule.id]).length;
  const total = PASSWORD_POLICY_RULES.length;

  if (passedCount === total && password.length > 0) {
    return { level: 'strong', label: 'Güçlü', percent: 100, color: '#2e7d5a' };
  }

  if (passedCount >= 2) {
    const percent = Math.round((passedCount / total) * 100);
    return { level: 'medium', label: 'Orta', percent: Math.max(percent, 40), color: '#d97706' };
  }

  const percent =
    password.length === 0 ? 10 : Math.max(Math.round((passedCount / total) * 100), 15);

  return { level: 'weak', label: 'Zayıf', percent, color: '#b42318' };
}
